// src/components/QuestCard.tsx
"use client";

import { Check, ArrowRight, Camera, Users, Calendar, MessageCircle, Twitter, FileText, Dice6, Clock, Star } from "lucide-react";
import { Button } from "@heroui/react";
import { TaskStatus, ChallengeTask, ChallengeCampaignEventState } from "@/types/challenges.types";
import { getMaxPoints } from "@/lib/challenges";
import Image from "next/image";
import { motion } from "framer-motion";

interface QuestCardProps {
  quest: TaskStatus;
  challengeTask: ChallengeTask | undefined;
  onClick: () => void;
  disabled?: boolean;
}



// Function to get the appropriate icon based on task type
const getTaskIcon = (taskName: string, title: string, description: string) => {
  const lowerTaskName = taskName.toLowerCase();
  const lowerTitle = title.toLowerCase();
  const lowerDescription = description.toLowerCase();
  
  // Check for create_media with "photo" in name or description
  if (lowerTaskName === 'create_media' && (lowerTitle.includes('photo') || lowerDescription.includes('photo'))) {
    return 'https://pulsarmoney.b-cdn.net/rhuna/photo.png';
  }
  
  // Map task types to icons
  switch (lowerTaskName) {
    case 'follow_twitter_account':
      return 'https://pulsarmoney.b-cdn.net/rhuna/x.png';
    case 'click_link':
      return 'https://pulsarmoney.b-cdn.net/rhuna/telegram.png';
    case 'retweet':
    case 'retweet_post':
      return 'https://pulsarmoney.b-cdn.net/rhuna/x.png';
    case 'join_discord':
    case 'discord_role':
      return 'https://pulsarmoney.b-cdn.net/rhuna/discord_icon_new.png';
    case 'create_media':
      return 'https://pulsarmoney.b-cdn.net/rhuna/media.png';
    case 'secret_code':
      return 'https://pulsarmoney.b-cdn.net/rhuna/telegram.png';
    case 'is_subscribed_to_newsletter':
      return 'https://pulsarmoney.b-cdn.net/rhuna/newsletter.png';
    case 'recurring_task':
      return 'https://pulsarmoney.b-cdn.net/rhuna/calendar.png';
    case 'goblin_query':
      return 'https://pulsarmoney.b-cdn.net/rhuna/calendar.png';
    case 'hyperion_query':
      return 'https://pulsarmoney.b-cdn.net/rhuna/calendar.png';
    case 'quiz':
      return 'https://pulsarmoney.b-cdn.net/rhuna/quiz.png';
    default:
      return null; // Will use default icon
  }
};

export const QuestCard = ({ quest, challengeTask, onClick, disabled = false }: QuestCardProps) => {
  const maxPoints = challengeTask ? getMaxPoints(challengeTask) : parseInt(quest.maxPoints) || 100;
  const isCompleted = quest.status === ChallengeCampaignEventState.SUCCESSFUL;
  const isPending = quest.status === ChallengeCampaignEventState.PENDING;
  
  // Get the appropriate icon for this task
  const taskIconUrl = getTaskIcon(quest.taskName, quest.title, quest.description);
  const isLargeIcon = quest.taskName === 'quiz';
  const IconComponent = Camera; // Default fallback

  // Strip markdown and truncate description for card preview
  const truncateDescription = (text: string, maxLength: number = 50) => {
    // Convert markdown links [text](url) to just text
    const plainText = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength) + "...";
  };

  const handleClick = () => {
    if (disabled) {
      alert("Please connect your wallet to start this quest!");
      return;
    }
    
    onClick();
  };

  return (
    <div 
      className={`bg-[#0C1113] border border-gray-800/50 rounded-lg p-4 sm:p-6 hover:border-gray-700/60 transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      onClick={disabled ? undefined : handleClick}
    >
      {/* Mobile Layout */}
      <div className="flex flex-col sm:hidden gap-3">
        {/* Top row: Icon + Title + Points */}
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center relative" style={{
              boxShadow: '0 0 20px rgba(96, 255, 138, 0.3), 0 0 40px rgba(96, 255, 138, 0.1)'
            }}>
              {taskIconUrl ? (
                <Image
                  src={taskIconUrl}
                  alt={`${quest.taskName} icon`}
                  width={isLargeIcon ? 32 : 20}
                  height={isLargeIcon ? 32 : 20}
                  className={isLargeIcon ? "w-8 h-8 object-contain" : "w-5 h-5 object-contain"}
                />
              ) : (
                <IconComponent className="w-5 h-5 text-green-400" />
              )}
            </div>
          </div>

          {/* Title and Points */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-base font-semibold text-white leading-tight">
                {quest.title}
              </h3>
              <span className="text-sm font-semibold text-[#60FF8A] flex-shrink-0 flex items-center gap-1">
                <Star className="w-3 h-3" />
                +{maxPoints} pts
              </span>
            </div>
          </div>
        </div>

        {/* Description */}
        <p className="text-gray-400 text-sm">
          {truncateDescription(quest.description)}
        </p>

        {/* Bottom row: Status + Action */}
        <div className="flex items-center justify-between">
          {/* Status */}
          <div className="flex items-center gap-2">
            {isCompleted && (
              <div className="flex items-center gap-2 text-green-400">
                <Check className="w-4 h-4" />
                <span className="text-sm font-medium">Completed</span>
              </div>
            )}
            {isPending && (
              <div className="flex items-center gap-2 text-yellow-400">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">Pending</span>
              </div>
            )}
          </div>

          {/* Action Button */}
          {!isCompleted && !isPending && (
            <motion.div
              whileHover={!disabled ? { scale: 1.05, y: -1 } : {}}
              whileTap={!disabled ? { scale: 0.95 } : {}}
            >
              <Button
                size="sm"
                className={`relative font-medium px-3 py-2 rounded-lg flex items-center justify-center text-sm transition-all duration-200 ${
                  disabled 
                    ? 'bg-gray-500 cursor-not-allowed text-gray-300' 
                    : 'bg-[#60FF8A] hover:bg-[#60FF8A]/90 text-black hover:shadow-lg'
                }`}
                onPress={handleClick}
                isDisabled={disabled}
                endContent={<ArrowRight className="w-3 h-3" />}
              >
                <span className="absolute inset-0 bg-white opacity-0 hover:opacity-10 transition-opacity duration-200 rounded-lg" />
                <span className="relative">
                  {disabled ? 'Connect' : 'Start'}
                </span>
              </Button>
            </motion.div>
          )}
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden sm:flex items-center gap-4">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center relative" style={{
            boxShadow: '0 0 20px rgba(96, 255, 138, 0.3), 0 0 40px rgba(96, 255, 138, 0.1)'
          }}>
            {taskIconUrl ? (
              <Image
                src={taskIconUrl}
                alt={`${quest.taskName} icon`}
                width={isLargeIcon ? 40 : 24}
                height={isLargeIcon ? 40 : 24}
                className={isLargeIcon ? "w-10 h-10 object-contain" : "w-6 h-6 object-contain"}
              />
            ) : (
              <IconComponent className="w-6 h-6 text-green-400" />
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-white mb-1">
            {quest.title}
          </h3>
          <p className="text-gray-400 text-sm mb-2">
            {truncateDescription(quest.description)}
          </p>
          
          {/* Progress indicator for in-progress quests */}
          {isPending && (
            <div className="text-sm text-gray-300">
              In Progress
            </div>
          )}
        </div>

        {/* Right side - Points, Action */}
        <div className="flex-shrink-0 flex flex-col items-end gap-3">
          {/* Points */}
          <div className="flex items-center gap-3">
            <span className="text-white font-semibold flex items-center gap-1">
              <Star className="w-4 h-4 text-[#60FF8A]" />
              +{maxPoints} pts
            </span>
          </div>

          {/* Action Button */}
          <div className="flex items-center gap-2">
            {isCompleted ? (
              <div className="flex items-center gap-2 text-green-400">
                <Check className="w-5 h-5" />
                <span className="text-sm font-medium">Completed</span>
              </div>
            ) : isPending ? (
              <div className="flex items-center gap-2 text-yellow-400">
                <Clock className="w-5 h-5" />
                <span className="text-sm font-medium">Pending</span>
              </div>
            ) : (
              <motion.div
                whileHover={!disabled ? { scale: 1.05, y: -1 } : {}}
                whileTap={!disabled ? { scale: 0.95 } : {}}
              >
                <Button
                  size="sm"
                  className={`relative font-medium px-4 py-2 rounded-lg flex items-center justify-center transition-all duration-200 ${
                    disabled 
                      ? 'bg-gray-500 cursor-not-allowed text-gray-300' 
                      : 'bg-[#60FF8A] hover:bg-[#60FF8A]/90 text-black hover:shadow-lg'
                  }`}
                  onPress={handleClick}
                  isDisabled={disabled}
                  endContent={<ArrowRight className="w-4 h-4" />}
                >
                  <span className="absolute inset-0 bg-white opacity-0 hover:opacity-10 transition-opacity duration-200 rounded-lg" />
                  <span className="relative">
                    {disabled ? 'Connect Wallet' : 'Start'}
                  </span>
                </Button>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
